/** Отзыв игрока — представление без стора, всё приходит пропсами. */
import type { FeedbackInput } from "./actions";
import { Button, Icon, WindowShell } from "../kit";
import { FEEDBACK_CONTACT_MAX, FEEDBACK_TEXT_MAX, FEEDBACK_TEXT_MIN, type FeedbackType } from "@newfungame/contracts";
import { useT } from "../../../app";
import { useState } from "preact/hooks";
import "./styles.css";

const FEEDBACK_TYPES = [
  { type: "bug", icon: "bug", label: "win.feedback.bug", hint: "win.feedback.hintBug" },
  { type: "idea", icon: "bulb", label: "win.feedback.idea", hint: "win.feedback.hintIdea" },
  { type: "other", icon: "chat", label: "win.feedback.other", hint: "win.feedback.hintOther" },
] as const;

export type FeedbackError = "failed" | "limit";

export interface FeedbackViewProps {
  busy: boolean;
  error: FeedbackError | null;
  done: boolean;
  onSend(input: FeedbackInput): void;
  onClose(): void;
}

export function FeedbackView({ busy, error, done, onSend, onClose }: FeedbackViewProps) {
  const t = useT();
  const [type, setType] = useState<FeedbackType>("bug");
  const [text, setText] = useState("");
  const [contact, setContact] = useState("");
  if (done) {
    return (
      <WindowShell kind="feedback" title={t("win.feedback.doneTitle")}>
        <Icon name="check" size={84} class="win-feedback__ok" />
        <p class="win-subtitle">{t("win.feedback.doneThanks")}</p>
        <p class="win-text">{t("win.feedback.doneText")}</p>
        <div class="win-actions">
          <Button onClick={onClose} data-testid="feedback-done">
            {t("win.feedback.doneClose")}
          </Button>
        </div>
      </WindowShell>
    );
  }
  const body = text.trim();
  const current = FEEDBACK_TYPES.find((f) => f.type === type) ?? FEEDBACK_TYPES[0];
  return (
    <WindowShell kind="feedback" title={t("win.feedback.title")}>
      <div class="win-feedback__types" role="group" aria-label={t("win.feedback.title")}>
        {FEEDBACK_TYPES.map((f) => (
          <button
            type="button"
            class="win-feedback__type"
            aria-pressed={f.type === type}
            data-testid={`feedback-type-${f.type}`}
            onClick={() => setType(f.type)}
          >
            <Icon name={f.icon} size={20} />
            {t(f.label)}
          </button>
        ))}
      </div>
      <textarea
        class="win-feedback__field win-feedback__text"
        placeholder={t(current.hint)}
        aria-label={t(current.hint)}
        maxLength={FEEDBACK_TEXT_MAX}
        value={text}
        data-testid="feedback-text"
        onInput={(e) => setText(e.currentTarget.value)}
      />
      <input
        class="win-feedback__field"
        placeholder={t("win.feedback.contact")}
        aria-label={t("win.feedback.contact")}
        maxLength={FEEDBACK_CONTACT_MAX}
        autocomplete="off"
        value={contact}
        data-testid="feedback-contact"
        onInput={(e) => setContact(e.currentTarget.value)}
      />
      <p class="win-note win-small">{t("win.feedback.note")}</p>
      {error && (
        <p class="win-feedback__error" role="alert" data-testid="feedback-error">
          {t(error === "limit" ? "win.feedback.errorLimit" : "win.feedback.errorFailed")}
        </p>
      )}
      <div class="win-actions">
        <Button
          disabled={busy || body.length < FEEDBACK_TEXT_MIN}
          onClick={() => onSend({ type, text: body, ...(contact.trim() ? { contact: contact.trim() } : {}) })}
          data-testid="feedback-send"
        >
          {t("win.feedback.send")}
        </Button>
        <Button tone="link" onClick={onClose} data-testid="feedback-close">
          {t("win.feedback.later")}
        </Button>
      </div>
    </WindowShell>
  );
}
